import { putUsersById } from '@blazeteams/sdk';
import { useAuthStore } from '@/store/auth-store';
import { usePresenceStore, type PresenceStatus } from './presence-store';

/**
 * Self-presence controller.
 *
 * Backend exposes `PUT /users/{id}` with body `{ presence }` and rebroadcasts
 * the change to the tenant via Socket.IO `user.presence_changed`. We do NOT
 * optimistically update peers — when the broadcast comes back, the realtime
 * handler will write our own status into the presence store like any other
 * user. We only optimistically update our own entry so the UI feels snappy.
 *
 * Manual override semantics:
 *   - User picks 'busy' → manualOverride = true → idle detection is suspended.
 *   - User picks 'online' explicitly → manualOverride cleared → idle resumes.
 *   - System auto-flips to 'away' on idle ONLY if no manual override is set.
 */

const IDLE_THRESHOLD_MS = 5 * 60 * 1000; // 5 min — matches Slack/Linear defaults
const ACTIVITY_DEBOUNCE_MS = 1500;

let manualOverride = false;
let lastActivityAt = Date.now();
let currentEmitted: PresenceStatus | null = null;
let activityDebounce: ReturnType<typeof setTimeout> | null = null;
let idleTimer: ReturnType<typeof setInterval> | null = null;
let activityListenersAttached = false;

function getMyId(): string | null {
  return useAuthStore.getState().user?.id ?? null;
}

async function emit(status: PresenceStatus): Promise<void> {
  // While a super_admin is impersonating a tenant there is no real "self"
  // user to update: the impersonation JWT carries user_id 0, but our stored
  // `user` is still the super_admin. Emitting would PUT /users/{super_admin
  // id} with the impersonation token, which the backend 401s — and because
  // impersonation tokens have no refresh token, that 401 tears the whole
  // session down (see the 401 interceptor in src/api/client.ts). Skip
  // self-presence entirely while impersonating.
  if (useAuthStore.getState().impersonation) return;
  const id = getMyId();
  if (!id) return;
  if (currentEmitted === status) return; // already in sync with backend
  // Optimistically reflect locally — the broadcast will reconfirm shortly.
  usePresenceStore.getState().setPresence(id, status);
  currentEmitted = status;
  try {
    const { error } = await putUsersById({ path: { id }, body: { presence: status } });
    if (error) {
      console.error('[Presence] Failed to publish status', status, error);
      currentEmitted = null; // allow retry on next change
    }
  } catch (e) {
    console.error('[Presence] Network error publishing status', e);
    currentEmitted = null;
  }
}

function handleActivity(): void {
  lastActivityAt = Date.now();
  if (activityDebounce) return; // already scheduled
  activityDebounce = setTimeout(() => {
    activityDebounce = null;
    // If we drifted to 'away' automatically, snap back to 'online' on activity.
    if (!manualOverride && currentEmitted === 'away') {
      void emit('online');
    }
  }, ACTIVITY_DEBOUNCE_MS);
}

function checkIdle(): void {
  if (manualOverride) return;
  if (currentEmitted !== 'online') return;
  if (Date.now() - lastActivityAt > IDLE_THRESHOLD_MS) {
    void emit('away');
  }
}

function attachActivityListeners(): void {
  if (activityListenersAttached || typeof window === 'undefined') return;
  activityListenersAttached = true;
  // Passive listeners — we only read timestamps, never preventDefault.
  window.addEventListener('mousemove', handleActivity, { passive: true });
  window.addEventListener('keydown', handleActivity);
  window.addEventListener('focus', handleActivity);
  window.addEventListener('click', handleActivity);
}

function detachActivityListeners(): void {
  if (!activityListenersAttached || typeof window === 'undefined') return;
  activityListenersAttached = false;
  window.removeEventListener('mousemove', handleActivity);
  window.removeEventListener('keydown', handleActivity);
  window.removeEventListener('focus', handleActivity);
  window.removeEventListener('click', handleActivity);
}

/**
 * Start the self-presence controller. Idempotent — safe to call again.
 * Called from AuthInitializer when the token appears.
 */
export function startSelfPresence(): void {
  attachActivityListeners();
  if (idleTimer === null) {
    idleTimer = setInterval(checkIdle, 30_000); // check every 30s
  }
  // Announce we're online. If the user previously set a manual status this
  // session, we respect it.
  if (!manualOverride) {
    void emit('online');
  }
}

/**
 * Stop the controller and announce offline. Called on logout / disconnect.
 * We deliberately fire-and-forget the offline emit — if the network is gone
 * the backend's socket teardown will mark us offline anyway.
 */
export function stopSelfPresence(options?: { announceOffline?: boolean }): void {
  detachActivityListeners();
  if (idleTimer) {
    clearInterval(idleTimer);
    idleTimer = null;
  }
  if (activityDebounce) {
    clearTimeout(activityDebounce);
    activityDebounce = null;
  }
  if (options?.announceOffline !== false) void emit('offline');
  manualOverride = false;
  currentEmitted = null;
}

/**
 * Manually set status. 'online' clears the override (idle detection resumes);
 * 'away' or 'busy' sets the override (idle detection is suspended until the
 * user picks 'online' again).
 */
export function setMyPresence(status: PresenceStatus): void {
  manualOverride = status !== 'online';
  lastActivityAt = Date.now();
  void emit(status);
}

/** Read my current published status (for menus/indicators). */
export function useMyPresence(): PresenceStatus {
  const myId = useAuthStore((s) => s.user?.id);
  return usePresenceStore((s) =>
    myId ? s.byUserId[myId]?.status ?? 'offline' : 'offline',
  );
}
